import React, { useState, ChangeEvent } from "react";
import { Plus, X } from "lucide-react";

interface FileUploadProps {
  value?: File | string | null;
  onChange?: (file: File | null) => void;
  className?: string;
  disabled?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  value,
  onChange,
  className = "",
  disabled = false,
}) => {
  const [internalFile, setInternalFile] = useState<File | null>(null);

  // Use external value if provided, otherwise use internal state
  const uploadedFile = value instanceof File ? value : internalFile;
  const displayFileName =
    uploadedFile?.name || (typeof value === "string" ? value : null);

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>): void => {
    if (disabled) return;

    const file = event.target.files?.[0];
    if (file && file.size <= 30 * 1024 * 1024) {
      if (onChange) {
        onChange(file);
      } else {
        setInternalFile(file);
      }
    }
  };

  const handleRemoveFile = (): void => {
    if (disabled) return;

    if (onChange) {
      onChange(null);
    } else {
      setInternalFile(null);
    }
  };

  const handleEditClick = (): void => {
    if (disabled) return;
    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    fileInput?.click();
  };

  const handleCardClick = (): void => {
    if (disabled) return;
    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    fileInput?.click();
  };

  const hasFile = uploadedFile || displayFileName;

  return (
    <div className={className}>
      <div className="relative">
        <div
          className={`relative w-64 h-20 overflow-hidden cursor-pointer group ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <div className="w-full h-full bg-white flex flex-col items-center justify-center transition-all duration-200 hover:bg-gray-50">
            <Plus size={28} className="text-gray-400 mb-2" />
            <span className="text-gray-500 text-sm">File (Max 30MB)</span>
          </div>

          {hasFile && (
            <>
              <div className="absolute inset-0 bg-white flex flex-col items-center justify-center">
                <div className="text-center px-2">
                  <div className="text-lg font-semibold mb-2">
                    File Uploaded
                  </div>
                  <div className="text-sm break-words">
                    {displayFileName || uploadedFile?.name}
                    {uploadedFile?.size && (
                      <span>
                        {" "}
                        ({(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB)
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {!disabled && (
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <button
                    onClick={handleEditClick}
                    className="px-8 py-2 bg-white text-gray-800 rounded-full font-medium hover:bg-gray-100 transition-colors"
                    type="button"
                  >
                    Edit
                  </button>
                </div>
              )}
            </>
          )}

          {!disabled && (
            <button
              className={`absolute top-2 right-2 p-1 rounded-full transition-colors z-20 ${
                hasFile
                  ? "bg-white/80 hover:bg-white text-gray-600"
                  : "hover:bg-gray-100 text-gray-400"
              }`}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handleRemoveFile();
              }}
              type="button"
            >
              <X size={16} />
            </button>
          )}

          {!disabled && (
            <div className="absolute inset-0" onClick={handleCardClick} />
          )}

          <input
            id="file-input"
            type="file"
            accept="*/*"
            onChange={handleFileUpload}
            className="hidden"
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
